import { Injectable, inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";

import { ConferenceMode } from "@auvious/rtc";

import { InteractionService } from "../../services/interaction.service";
import {
  ErrorPageCode,
  PARAM_CALL_TYPE,
  PARAM_COBROWSE_AVAILABLE,
  PARAM_CONVERSATION_DESTINATION,
  PARAM_CONVERSATION_ID,
  PARAM_CONVERSATION_ORIGIN,
  PARAM_KIOSK_MODE,
  PARAM_ROOM_ID,
  PARAM_WIDGET_ORIGIN_URL,
  PARAM_CUSTOMER_ID,
  DEFAULT_CUSTOMER_ID,
  PARAM_CONVERSATION_CHANNEL,
  PARAM_MESSAGING,
  PARAM_WHISPER_MODE,
  PARAM_APPOINTMENT_GUEST,
} from "../../app.enums";
import { Interaction } from "../../models/Interaction";

import {
  ApplicationService,
  ApplicationTypeEnum,
  ConversationOriginEnum,
  AuviousRtcService,
  ConversationTypeEnum,
  UserRoleEnum,
  UserService,
  AppConfigService,
  ConversationDestinationEnum,
  UserCapabilityEnum,
  ConversationMetadataEnum,
  CLAIM_CONFERENCE_ID,
  CLAIM_INTERACTION_ID,
  OriginModeEnum,
  ConversationChannelEnum,
  AppointmentService,
  CLAIM_APPOINTMENT_ID,
  WhisperModeEnum,
  IAppointment,
  IAppointmentRouting,
  ConferenceService,
  CLAIM_CHANNEL,
  CustomerParam,
} from "../../../core-ui";
import { AuthState } from "../../../core-ui/models/AuthState";
import { IInteraction } from "../../../core-ui/models/IInteraction";
import { AnalyticsService } from "../../../core-ui/services/analytics.service";
import { WindowService } from "../../../core-ui/services/window.service";
import { GenesysInteraction } from "../../../app/models";
import { InvitationService } from "../../../app/services";

@Injectable({ providedIn: "root" })
export class InteractionRoomResolver {
  private conferenceService = inject(ConferenceService);
  private interactionService = inject(InteractionService);
  private applicationService = inject(ApplicationService);
  private rtcService = inject(AuviousRtcService);
  private router = inject(Router);
  private userService = inject(UserService);
  private analyticsService = inject(AnalyticsService);
  private appointmentService = inject(AppointmentService);
  private invitation = inject(InvitationService);
  private config = inject(AppConfigService);
  private windowService = inject(WindowService);

  private authState: AuthState;

  private findParam(
    route: ActivatedRouteSnapshot,
    param: string,
    authStateParam: string,
  ): string {
    if (route.queryParamMap.has(param)) {
      return route.queryParamMap.get(param);
    }
    if (this.authState && authStateParam in this.authState) {
      return this.authState[authStateParam];
    }
    if (route.queryParamMap.has("state")) {
      this.authState = AuthState.fromSerializedState(
        route.queryParamMap.get("state"),
      );
      if (authStateParam in this.authState) {
        return this.authState[authStateParam];
      }
    }
    return null;
  }

  /**
   * a popped out call window fetches the interaction itself. When that fetch has no
   * phone number for the customer - or failed and left us a placeholder - use the one
   * the agent's window had, as long as it is about the same conversation.
   */
  private applyPopOutHandOver(interaction: IInteraction) {
    const handOver = this.windowService.readPopOutHandOver();
    if (
      !!handOver?.customerPhone &&
      handOver.conversationId === interaction.getId() &&
      !interaction.getCustomerPhoneNumber()
    ) {
      interaction.setCustomerPhoneNumber?.(handOver.customerPhone);
    }
  }

  private setAppointmentMetadataToInteraction(
    interaction: IInteraction,
    appointment: IAppointment<IAppointmentRouting>,
  ): IInteraction {
    this.appointmentService.notifyChange(appointment);
    if (appointment.metadata) {
      Object.keys(appointment.metadata).forEach((key) => {
        interaction.setMetadataItem(
          key as ConversationMetadataEnum,
          appointment.metadata[key],
        );
      });
    }
    return interaction;
  }

  async resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<IInteraction> {
    let interaction: IInteraction;
    const user = this.userService.getActiveUser();

    if (user.hasRole(UserRoleEnum.customer)) {
      interaction =
        this.interactionService.getActiveInteraction() ||
        new Interaction(
          user.getClaim(CLAIM_CONFERENCE_ID),
          user.getClaim(CLAIM_INTERACTION_ID),
        );
      this.interactionService.setActiveInteraction(interaction);
    } else {
      if (this.interactionService.isActiveInteractionAvailable()) {
        interaction = this.interactionService.getActiveInteraction();
        if (!!interaction.getAppointmentId()) {
          try {
            const whisperMode = this.findParam(
              route,
              PARAM_WHISPER_MODE,
              "whisperMode",
            );

            let appointment;
            if (
              this.userService.isAgent &&
              !this.userService.isGuestAgent && // guest agents are in a new tab so this code is unreachable, but let's add it for future use
              !(this.userService.isSupervisor && !!whisperMode) // monitoring supervisors are in new tab, same as above
            ) {
              // add agent as participant in case another agent (not the assigned one) answers the interaction
              appointment = await this.appointmentService.acceptInteraction(
                interaction.getAppointmentId(),
              );
            } else {
              // just get the appointment if guest agent or monitoring supervisor
              appointment = await this.appointmentService.get(
                interaction.getAppointmentId(),
              );
            }

            // then update interaction
            interaction = this.setAppointmentMetadataToInteraction(
              interaction,
              appointment,
            );
          } catch (ex) {
            // nothing, interaction remains intact
          }
        }
        this.analyticsService.trackCallRequested(interaction, {
          supervisorUrl: this.invitation.prepareSupervisorInvitation(
            interaction,
            WhisperModeEnum.MONITOR,
          ),
        });
        return interaction;
      }

      // joining as a guest agent or refresh
      const interactionId = this.findParam(
        route,
        PARAM_CONVERSATION_ID,
        "conversationId",
      );
      if (!!interactionId) {
        interaction = await this.interactionService.retrieveInteractionData(
          interactionId,
        );
      }

      // construct interaction from query params
      const room = this.findParam(route, PARAM_ROOM_ID, "room");
      if (!!room) {
        if (!!interaction) {
          interaction.setRoom(room);
        } else {
          if (
            user.hasRole(UserRoleEnum.agent) &&
            this.applicationService.getActiveApplication().getType() ===
              ApplicationTypeEnum.GenesysCloud
          ) {
            interaction = new GenesysInteraction({
              room,
              legacy: true,
              communicationId: interactionId ?? room,
              conversationId: interactionId ?? room,
              customerId: DEFAULT_CUSTOMER_ID,
              agentId: user.getId(),
              origin: ConversationOriginEnum.CHAT,
              type: ConversationTypeEnum.videoCall,
              channel: ConversationChannelEnum.unknown,
              customerEmail: "replace-me@auvious.video",
              customerPhone: "",
            });
          } else {
            interaction = new Interaction(room, interactionId);
            (interaction as Interaction).setAgentId(user.getId());
          }

          switch (this.applicationService.getActiveApplication().getType()) {
            case ApplicationTypeEnum.Test:
            case ApplicationTypeEnum.StandaloneOpenID:
            case ApplicationTypeEnum.GenesysCloud:
              // agent creates the interaction
              interaction.setOrigin(ConversationOriginEnum.EMBEDDED);
              break;
          }
        }
      }

      if (!!interaction) {
        this.applyPopOutHandOver(interaction);
      }

      // if we haven't managed to discover an interaction yet, create one
      if (!interaction) {
        switch (this.applicationService.getActiveApplication().getType()) {
          case ApplicationTypeEnum.Test:
          case ApplicationTypeEnum.StandaloneOpenID:
            const conference = await this.rtcService.createConference({
              mode: ConferenceMode.ROUTER,
              interactionId,
            });
            interaction = new Interaction(conference.name, interactionId);
            (interaction as Interaction).setAgentId(user.getId());
            this.conferenceService.setActiveConference(conference);
            break;
        }
      }
    }

    if (!interaction) {
      this.router.navigate(["/error", ErrorPageCode.ROOM_NOT_FOUND], {
        queryParamsHandling: "preserve",
      });
    }

    const kioskMode = this.findParam(route, PARAM_KIOSK_MODE, "kioskMode");
    if (kioskMode === "true") {
      interaction.setOriginMode(OriginModeEnum.kiosk);
    }

    const parentFrameUrl = this.findParam(route, PARAM_WIDGET_ORIGIN_URL, "");
    if (parentFrameUrl) {
      interaction.setParentFrameUrl(parentFrameUrl);
      interaction.setOrigin(ConversationOriginEnum.WIDGET);
    }

    const type = this.findParam(route, PARAM_CALL_TYPE, "conversationType");
    if (type) {
      interaction.setType(type as ConversationTypeEnum);
    }

    const origin = this.findParam(
      route,
      PARAM_CONVERSATION_ORIGIN,
      "conversationOrigin",
    );
    if (origin) {
      interaction.setOrigin(origin as ConversationOriginEnum);
    }

    if (user.hasClaim(CLAIM_APPOINTMENT_ID)) {
      interaction.setOrigin(ConversationOriginEnum.APPOINTMENT);
      interaction.setAppointmentId(user.getClaim(CLAIM_APPOINTMENT_ID));
      if (
        // guests are not connected to conversation
        route.queryParamMap.get(PARAM_APPOINTMENT_GUEST) !== "1" &&
        this.config.customerParamEnabled(CustomerParam.APPOINTMENT_CHAT_ENABLED)
      ) {
        user.addCapability(UserCapabilityEnum.messaging);
      }
    }

    const destination = this.findParam(
      route,
      PARAM_CONVERSATION_DESTINATION,
      "conversationDestination",
    );
    if (destination) {
      interaction.setDestination(destination as ConversationDestinationEnum);
    }

    const channel = this.findParam(
      route,
      PARAM_CONVERSATION_CHANNEL,
      "conversationChannel",
    );
    if (channel) {
      interaction.setChannel(channel as ConversationChannelEnum);
    }

    if (user.hasClaim(CLAIM_CHANNEL)) {
      interaction.setChannel(
        user.getClaim(CLAIM_CHANNEL) as ConversationChannelEnum,
      );
    }

    const cobrowseAvailable = this.findParam(
      route,
      PARAM_COBROWSE_AVAILABLE,
      "",
    );
    if (cobrowseAvailable === "true") {
      user.addCapability(UserCapabilityEnum.coBrowse);
      // the co-browsed page can be sketched on
      user.addCapability(UserCapabilityEnum.sketch);
    }

    const messagingEnabled = this.findParam(route, PARAM_MESSAGING, "");
    if (messagingEnabled === "true") {
      user.addCapability(UserCapabilityEnum.messaging);
    }

    const customerId = this.findParam(route, PARAM_CUSTOMER_ID, "customerId");
    if (customerId) {
      interaction.setCustomerId(customerId);
    }

    const whisperMode = this.findParam(
      route,
      PARAM_WHISPER_MODE,
      "whisperMode",
    );
    if (whisperMode && this.userService.isSupervisor) {
      interaction.setWhisperMode(whisperMode as WhisperModeEnum);
    }

    this.interactionService.setActiveInteraction(interaction);

    if (
      user.hasRole(UserRoleEnum.agent) &&
      !user.hasRole(UserRoleEnum.guestAgent)
    ) {
      this.analyticsService.trackCallRequested(interaction, {
        supervisorUrl: this.invitation.prepareSupervisorInvitation(
          interaction,
          (whisperMode as WhisperModeEnum) || WhisperModeEnum.MONITOR,
        ),
      });
    }
    return interaction;
  }
}
